import * as Sentry from "@sentry/react";

export enum Role {
    NONE = "none",
    MANAGER = "manager",
    OWNER = "owner",
    ADMINISTRATOR = "administrator",
    INSPECTION_USER = "inspectionuser",
    INSPECTION_ADMIN = "inspectionadmin",
    ECADMIN = "ecadmin",
    COOWNER = "coowner",
    ECMANAGER = "ecmanager"
}

export enum ErpType {
    RIMO = 1,
    IT2 = 2
}

interface RoleMapEntry {
    key: string;
    role: Role;
    rootRoute: string;
    label: string;
    roleid: number;
}

export const RolesMap: Array<RoleMapEntry> = [
    { key: "manager", role: Role.MANAGER, rootRoute: "/", label: "Bewirtschafter", roleid: 30 },
    { key: "owner", role: Role.OWNER, rootRoute: "/", label: "Eigentümer", roleid: 31 },
    { key: "coowner", role: Role.COOWNER, rootRoute: "/", label: "Stockwerkeigentümer", roleid: 32 },
    { key: "administrator", role: Role.ADMINISTRATOR, rootRoute: "/", label: "Administrator", roleid: 39 },
    {
        key: "inspectionuser",
        role: Role.INSPECTION_USER,
        rootRoute: "/",
        label: "Abnahme Benutzer",
        roleid: 20
    },
    {
        key: "inspectionadmin",
        role: Role.INSPECTION_ADMIN,
        rootRoute: "/",
        label: "Abnahme Administrator",
        roleid: 21
    },
    { key: "ecmanager", role: Role.ECMANAGER, rootRoute: "/", label: "EasyContact Manager", roleid: 51 },
    { key: "ecadmin", role: Role.ECADMIN, rootRoute: "/", label: "EasyContact Administrator", roleid: 52 },
    { key: "none", role: Role.NONE, rootRoute: "/", label: "", roleid: 0 }
];

const getValidRole = (role: string): Role => {
    const validRole = RolesMap.find((r) => r.key === role);
    return validRole ? validRole.role : Role.NONE;
};

const getSubdomainInToken = (hasuraClaimSubdomain: string): string => {
    if (!hasuraClaimSubdomain || hasuraClaimSubdomain === undefined) {
        return "dev";
    }

    const hasuraClaimSplits = hasuraClaimSubdomain.split(",");
    // case 1: "x-hasura-05": "remo1,dev"
    // case 2: "x-hasura-05": "dev,remo1"
    // case 3: "x-hasura-05": "admin,prod"
    const environments = ["dev", "test", "prod"];
    if (hasuraClaimSplits.length > 1) {
        if (environments.includes(hasuraClaimSplits[0])) {
            // return the non-environment-split, e.g. remo1
            return hasuraClaimSplits[hasuraClaimSplits.length - 1];
        }
    }

    return hasuraClaimSplits[0];
};

export const getRoleKey = (role: Role | undefined) => {
    const currentRole = RolesMap.find((r) => r.role === role);
    return currentRole ? currentRole.key : "";
};

export const getRoleLabel = (role: Role) => {
    return RolesMap.find((r) => r.role === role)?.label || "Unbekannt";
};

export class User {
    role: Role = Role.NONE;
    availableRoles: Role[] = [];
    availableRoleIds: number[] = [];
    userid?: string;
    username?: string;
    customerid?: string;
    erpType?: ErpType;
    hasECAdminRole: boolean = false;
    hasECManagerRole: boolean = false;

    error(message: string) {
        console.error(message);
    }

    async init(access_token: string) {
        if (access_token) {
            const token = await this.parseJwt(access_token);

            this.username = token.email;
            this.customerid = token.hasOwnProperty("xhasura01") ? token["xhasura01"] : undefined;
            this.userid = token.hasOwnProperty("xhasura03") ? token["xhasura03"] : undefined;


            if (token.hasOwnProperty("roles")) {
                const roles = token["roles"];

                if (roles.length > 0) {
                    this.availableRoles = roles.filter((role: string) => getValidRole(role) !== Role.NONE);
                }

                if (this.availableRoles.includes(Role.ECADMIN)) {
                    this.hasECAdminRole = true;
                }

                if (this.availableRoles.includes(Role.ECMANAGER)) {
                    this.hasECManagerRole = true;
                    this.role = Role.ECMANAGER;
                }

                if (this.hasECManagerRole) {
                    let subdomainErrorMsg = "";
                    if (token.hasOwnProperty("xhasura05")) {
                        const host: string = window?.location?.origin;
                        const subdomainInHost: string =
                            host.indexOf("http://localhost:") >= 0 ? "dev" : host.split(".")[0].replace("https://", "");
                        const subdomainInToken: string =
                            host.indexOf("http://localhost:") >= 0 ? "dev" : getSubdomainInToken(token["xhasura05"]);
                        if (subdomainInHost !== subdomainInToken) {
                            const errorMsgBegin = "subdomain of token does not match subdomain of host. details:";
                            subdomainErrorMsg = `${errorMsgBegin} subdomain of token is '${subdomainInToken}', host is '${host}.`;
                        }
                    } else {
                        subdomainErrorMsg = "x-hasura-subdomain not found.";
                    }
                    if (subdomainErrorMsg !== "") {
                        this.error(subdomainErrorMsg);
                        this.availableRoles = [];
                        this.availableRoleIds = [];
                    }
                }

                Sentry.configureScope((scope) => {
                    scope.setUser({ id: this.userid, username: this.username });
                });
            } else {
                this.error("'roles' claim not found");
                this.availableRoles = [];
                this.availableRoleIds = [];
            }
        }
    }

    private async parseJwt(token: string) {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );

        return JSON.parse(jsonPayload);
    }
}
